/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { accessAPI, downloadFile, Sidebar, CardCertificadoAcreditado, useFuncionalidad, tituloBase, TablaFiltrable } from "../../shared";
import ProductoEnCertificado from "./ProductoEnCertificado";
import { Boton } from "../../shared";
import { Titulo, EstadoCertificado } from "../../shared";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { CampoInformacion, ActividadesTimeline, ArchivoPreview } from "../../shared";
import DescargasDelCertificado from "./DescargasDelCertificado";
import { fechaCorta } from "../../portal/utilidades";

export default function CertificadoSeleccionado() {
  const [loader, setLoader] = useState(true);
  const [detallesCertificado, setDetallesCertificado] = useState();
  // Título de la pestaña, como en el admin: el de este registro cuando carga
  useEffect(() => {
    if (detallesCertificado) document.title = `Certificado ${detallesCertificado.numero}`;
  }, [detallesCertificado]);
  useEffect(() => () => (document.title = tituloBase()), []);

  const [loaderDescarga, setLoaderDescarga] = useState(false);
  const [loaderDescargaMarca, setLoaderDescargaMarca] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [extensionesSolicitadas, setExtensionesSolicitadas] = useState([]);

  const toast = useRef(null);
  const navigate = useNavigate();
  const menuRefsExt = useRef({});

  // Carga el id del certificado de la URL
  const { certificadoId } = useParams();
  // El PIN de envío a URSEA solo aplica a organismos con esa funcionalidad
  const conUrsea = useFuncionalidad("URSEA");

  // Cuando carga el componente va a buscar los detalles del certificado
  useEffect(() => {
    accessAPI(
      "GET",
      `cliente/instanciaCertificado/${certificadoId}`,
      null,
      (respuesta) => {
        setDetallesCertificado(respuesta);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg ||
            "Ocurrió un error inesperado, intente nuevamente más tarde.",
          life: 3000,
        });
      }
    );
  }, [certificadoId]);

  useEffect(() => {
    if (detallesCertificado) {
      setLoader(false);
    }
  }, [detallesCertificado]);

  // El titular solicita la baja de una extensión. No la cancela: crea una
  // solicitud que el admin resuelve desde su portada.
  function solicitarBajaExtension(extensionId) {
    accessAPI(
      "POST",
      `cliente/instanciacertificado/solicitar-baja-extension/${extensionId}`,
      null,
      () => {
        setExtensionesSolicitadas((prev) => [...prev, extensionId]);
        toast.current?.show({
          severity: "success",
          summary: "Solicitud enviada",
          detail: "Se solicitó la baja de la extensión al administrador.",
          life: 4000,
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "No se pudo enviar la solicitud.",
          life: 4000,
        });
      }
    );
  }

  // Descarga el certificado: el PDF siempre sale de la última emisión
  // (arquitectura actual, tabla Archivo)
  function descargarCertificado() {
    setLoaderDescarga(true);
    if (detallesCertificado.emisioncertificado[0]?.archivopdf?.nombredescarga) {
      downloadFile(
        `cliente/instanciacertificado/descargarpdf/${detallesCertificado.id}`,
        `Certificado ${detallesCertificado.numero}.pdf`,
        (response) => {
          setLoaderDescarga(false);
        },
        (respuesta) => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: respuesta[0]?.msg || "Error al descargar el certificado",
            life: 3000,
          });
          setLoaderDescarga(false);
        }
      );
    } else {
      toast.current?.show({
        severity: "warn",
        summary: "Advertencia",
        detail: "Este certificado no tiene archivo",
        life: 3000,
      });
      setLoaderDescarga(false);
    }
  }

  function descargarEtiqueta(etiquetaId, nombreArchivoPdf) {
    setLoaderDescarga(true);
    downloadFile(
      `cliente/instanciacertificado/descargaretiqueta/${etiquetaId}`,
      nombreArchivoPdf,
      (response) => {
        setLoaderDescarga(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar la etiqueta",
          life: 3000,
        });
        setLoaderDescarga(false);
      }
    );
  }

  function descargarMarca() {
    setLoaderDescargaMarca(true);
    downloadFile(
      `cliente/instanciacertificado/descargarmarca/${detallesCertificado.id}`,
      `Marca de conformidad - ${detallesCertificado.numero}.pdf`,
      (response) => {
        setLoaderDescargaMarca(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar la marca",
          life: 3000,
        });
        setLoaderDescargaMarca(false);
      }
    );
  }

  function descargarMarcadoProducto() {
    setLoaderDescargaMarca(true);
    downloadFile(
      `cliente/instanciacertificado/descargaretiquetadatos/${detallesCertificado.id}`,
      `Marcado datos - ${detallesCertificado.numero}.pdf`,
      (response) => {
        setLoaderDescargaMarca(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg || "Error al descargar el marcado del producto",
          life: 3000,
        });
        setLoaderDescargaMarca(false);
      }
    );
  }

  return (
    <main>
      <Toast ref={toast} position="top-center" />
      {loader && (
        <>
          <Titulo urlVolver="/certificados">
            <Skeleton width="300px" height="2rem" />
          </Titulo>
          <div className="grid">
            <div
              className="col-12 xl:col-6 lg:col-6 md:col-12 sm:col-12"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              <Card>
                <Skeleton width="100%" height="4rem" css={{ marginBottom: "1rem" }} />
                <Skeleton width="60%" height="1.5rem" />
              </Card>
              <Card>
                <div className="grid">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="col-6" css={{ marginBottom: "1rem" }}>
                      <Skeleton width="100%" height="1rem" css={{ marginBottom: "0.5rem" }} />
                      <Skeleton width="80%" height="1.5rem" />
                    </div>
                  ))}
                </div>
              </Card>
              <Card>
                <Skeleton width="100%" height="8rem" css={{ marginBottom: "1rem" }} />
                <Skeleton width="100%" height="4rem" />
              </Card>
            </div>
            <div
              className="col-12 xl:col-6 lg:col-6 md:col-12 sm:col-12"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              <Card>
                <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  {[...Array(3)].map((_, i) => (
                    <div key={i}>
                      <Skeleton width="100%" height="3rem" />
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}
      {!loader && detallesCertificado && (
        <div>
          <Titulo
            subtitulo={detallesCertificado.proceso?.nombrefantasia}
            urlVolver="/certificados"
            // Sin el punto de estado: el estado ya está en la tarjeta "Vigente"
            // Un certificado suspendido o anulado no ofrece descargas (PDF,
            // marca de conformidad, etiquetas)
            botones={
              estadoParaBanner(detallesCertificado).vigente && (
                <Boton callback={() => setSidebarVisible(true)} transparente>
                  Descargas
                </Boton>
              )
            }
          >
            Detalles del certificado {detallesCertificado.numero}
          </Titulo>
          <div className="grid">
            <div
              className="col-12 xl:col-6 lg:col-6 md:col-12 sm:col-12"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              {/* Estado real del certificado con el mismo componente y la misma
                  regla que el admin (el portal ya muestra también los no vigentes) */}
              <EstadoCertificado
                {...estadoParaBanner(detallesCertificado)}
                sinRenovacion={detallesCertificado.proceso.certificadotipo.sinrenovacion}
              />
              <Card>
                <Titulo>{detallesCertificado.proceso.nombrefantasia}</Titulo>
                <div className="grid">
                  {/* Solo si el cliente es el titular: en un certificado extendido el
                      proceso es de otra empresa */}
                  {!detallesCertificado.extendido && (
                    <CampoInformacion nombreCampo="Proceso">
                      {detallesCertificado.proceso?.nombre}
                    </CampoInformacion>
                  )}
                  <CampoInformacion nombreCampo="Tipo de certificado">
                    {detallesCertificado.proceso.certificadotipo.nombre}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Reglamentación">
                    {detallesCertificado.proceso.certificadotipo.reglamentacion}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Norma">
                    {detallesCertificado.proceso.norma.numero}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Fecha de emisión">
                    {fechaCorta(detallesCertificado.fechaemision)}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Fecha de vencimiento">
                    {fechaCorta(detallesCertificado.fechavencimiento)}
                  </CampoInformacion>
                  {!detallesCertificado.extendido && (
                    <>
                      <CampoInformacion nombreCampo="Fabricante">
                        {detallesCertificado.proceso.sitioFabricante?.nombre || "-"}
                      </CampoInformacion>
                      <CampoInformacion nombreCampo="Dirección del fabricante">
                        {detallesCertificado.proceso.sitioFabricante?.direccion || "-"}
                      </CampoInformacion>
                      <CampoInformacion nombreCampo="Fábrica">
                        {detallesCertificado.proceso.sitioFabrica?.nombre || "-"}
                      </CampoInformacion>
                      <CampoInformacion nombreCampo="Dirección de la fábrica">
                        {detallesCertificado.proceso.sitioFabrica?.direccion || "-"}
                      </CampoInformacion>
                    </>
                  )}
                  {conUrsea && (
                    <CampoInformacion nombreCampo="PIN">
                      {detallesCertificado.pinenvioursea}
                    </CampoInformacion>
                  )}
                </div>
              </Card>
              {detallesCertificado.extensioncertificado?.length > 0 && (
                <Card>
                  <Titulo>Certificado extendido</Titulo>
                  <TablaFiltrable conFiltros={false}
                    value={detallesCertificado.extensioncertificado}
                    size="small"
                    stripedRows
                    emptyMessage="Sin extensiones"
                  >
                    <Column
                      header="Empresa"
                      body={(ext) =>
                        ext.clienteextendido?.nombrefantasia ||
                        ext.clienteextendido?.razonsocial ||
                        "-"
                      }
                    />
                    <Column
                      header="Fecha de extensión"
                      body={(ext) => fechaCorta(ext.fechaextension) || "-"}
                    />
                    <Column
                      body={(ext) => {
                        const yaSolicitada =
                          ext.solicitudesBaja?.length > 0 ||
                          extensionesSolicitadas.includes(ext.id);
                        const items = [
                          yaSolicitada
                            ? {
                                label: "Solicitud de baja pendiente",
                                icon: "pi pi-clock",
                                disabled: true
                              }
                            : {
                                label: "Solicitar baja de extensión",
                                icon: "pi pi-trash",
                                command: () => solicitarBajaExtension(ext.id)
                              }
                        ];
                        return (
                          <div>
                            <Menu
                              // El Menu popup de PrimeReact cachea su model una vez
                              // montado; con este key se remonta cuando cambian los
                              // ítems (ej. pasa a "Solicitud de baja pendiente").
                              key={items.map((i) => i.label).join("|")}
                              model={items}
                              popup
                              ref={(el) => (menuRefsExt.current[ext.id] = el)}
                              id={`menu_ext_${ext.id}`}
                            />
                            <Boton
                              icono
                              chico
                              negro
                              callback={(event) =>
                                menuRefsExt.current[ext.id]?.toggle(event)
                              }
                            >
                              <i className="pi pi-ellipsis-v"></i>
                            </Boton>
                          </div>
                        );
                      }}
                      style={{ width: "60px", textAlign: "center" }}
                    />
                  </TablaFiltrable>
                </Card>
              )}
              <CardCertificadoAcreditado
                vinculos={
                  detallesCertificado.proceso.norma.norma_x_acreditacion
                }
              />
            </div>

            <div
              className="col-12 xl:col-6 lg:col-6 md:col-12 sm:col-12"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              {!detallesCertificado.extendido && detallesCertificado.proceso.actividad.length > 0 && (
                <Card>
                  <Titulo>Actividades y emisiones</Titulo>
                  <ActividadesTimeline
                    actividades={detallesCertificado.proceso.actividad}
                    detalleComoDialogo
                  />
                </Card>
              )}
              {!detallesCertificado.extendido && detallesCertificado.proceso.solicitudproceso?.PropuestaComercial
                ?.length > 0 && (
                <Card>
                  <Titulo>Propuestas comerciales</Titulo>
                  <TablaFiltrable conFiltros={false}
                    value={detallesCertificado.proceso.solicitudproceso.PropuestaComercial}
                    size="small"
                    stripedRows
                    selectionMode="single"
                    onRowSelect={(e) => navigate(`/propuesta/${e.data.id}?volver=/certificado/${certificadoId}`)}
                    css={{ "& .p-datatable-tbody > tr": { cursor: "pointer" } }}
                  >
                    <Column
                      header="Fecha"
                      body={(rowData) => fechaCorta(rowData.fechaemision)}
                    />
                    <Column
                      header="Estado"
                      body={(rowData) =>
                        rowData.fechaaceptacion ? (
                          <small css={{ color: "var(--verde)" }}>
                            Aceptada el {fechaCorta(rowData.fechaaceptacion)}
                          </small>
                        ) : (
                          <small css={{ color: "var(--amarillo)" }}>
                            Pendiente de aceptación
                          </small>
                        )
                      }
                    />
                  </TablaFiltrable>
                </Card>
              )}
              {!detallesCertificado.extendido && detallesCertificado.proceso.archivos?.length > 0 && (
                <Card>
                  <Titulo>Archivos del proceso</Titulo>
                  <TablaFiltrable conFiltros={false}
                    value={detallesCertificado.proceso.archivos}
                    size="small"
                    stripedRows
                  >
                    <Column
                      field="nombredescarga"
                      header="Nombre"
                      body={(rowData) => (
                        <ArchivoPreview
                          nombre={rowData.nombredescarga}
                          hash={rowData.hash}
                        />
                      )}
                    />
                  </TablaFiltrable>
                </Card>
              )}
            </div>
            <div
              className="col-12"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              {detallesCertificado.proceso.producto.length > 0 && (
                <Card>
                  <Titulo>Productos</Titulo>
                  <ProductoEnCertificado
                    productosCompletos={detallesCertificado.proceso.producto}
                    camposParaTabla={
                      detallesCertificado.proceso.camposParaTabla
                    }
                    productosParaTabla={
                      detallesCertificado.proceso.productosParaTabla
                    }
                  />
                </Card>
              )}
            </div>
          </div>
          <Sidebar
            visible={sidebarVisible}
            onHide={() => setSidebarVisible(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Descargas</Titulo>
            <DescargasDelCertificado
              detallesCertificado={detallesCertificado}
              loaderDescarga={loaderDescarga}
              loaderDescargaMarca={loaderDescargaMarca}
              descargarCertificado={descargarCertificado}
              descargarMarca={descargarMarca}
              descargarMarcadoProducto={descargarMarcadoProducto}
              descargarEtiqueta={descargarEtiqueta}
            />
          </Sidebar>
        </div>
      )}
    </main>
  );
}

// Misma regla que el admin (secciones/certificado/Certificado.js): vigente;
// con baja definitiva, anulado; cualquier otro estado no vigente, suspendido
function estadoParaBanner(certificado) {
  const estado = certificado.estadocertificado;
  if (!estado || estado.vigentebool) return { vigente: true, fechaEmision: certificado.fechaemision };
  if (estado.bajadefinitiva) return { anulado: true };
  return { suspendido: true };
}
